import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  LinearProgress,
  Chip,
  Grid,
  Card,
  CardContent,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  HourglassEmpty as PendingIcon,
  PlayArrow as RunningIcon,
  Replay as RetryIcon,
  ElectricBolt as CircuitIcon,
  Groups as PoolIcon,
} from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

interface WorkflowMetrics {
  completed_tasks: number;
  failed_tasks: number;
  execution_time: number;
  total_tasks: number;
}

interface AgentPool {
  pool_size: number;
  active: number;
  healthy: boolean;
}

interface CircuitBreaker {
  state: 'open' | 'closed' | 'half_open';
  failure_count: number;
  success_count: number;
}

interface SystemHealth {
  agent_pools: { [key: string]: AgentPool };
  circuit_breakers: { [key: string]: CircuitBreaker };
}

interface CeleryStatus {
  workflow_id: string;
  celery_task_id: string | null;
  state: 'PENDING' | 'STARTED' | 'SUCCESS' | 'FAILURE' | 'RETRY';
  status: string;
  message?: string;
  info?: string;
  result?: any;
  error?: string;
  traceback?: string;
  metrics?: WorkflowMetrics;
  retry_info?: {
    attempts: number;
    reason: string;
  };
  system_health?: SystemHealth;
}

interface WorkflowMonitorProps {
  workflowId: string;
  autoRefresh?: boolean;
  refreshInterval?: number;
}

const stateColors = {
  PENDING: 'default',
  STARTED: 'primary',
  SUCCESS: 'success',
  FAILURE: 'error',
  RETRY: 'warning',
} as const;

const stateIcons = {
  PENDING: <PendingIcon />,
  STARTED: <RunningIcon />,
  SUCCESS: <SuccessIcon />,
  FAILURE: <ErrorIcon />,
  RETRY: <RetryIcon />,
};

const circuitStateColors = {
  closed: 'success',
  open: 'error',
  half_open: 'warning',
} as const;

export default function WorkflowMonitor({ workflowId, autoRefresh = true, refreshInterval = 2000 }: WorkflowMonitorProps) {
  const [status, setStatus] = useState<CeleryStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadStatus();

    if (autoRefresh) {
      const interval = setInterval(loadStatus, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [workflowId, autoRefresh, refreshInterval]);

  const loadStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/workflows/${workflowId}/celery-status`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
        },
      });
      setStatus(response.data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load workflow status');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !status) {
    return <LinearProgress />;
  }

  if (error && !status) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!status) {
    return null;
  }

  const progressPercentage = status.metrics
    ? Math.round((status.metrics.completed_tasks / status.metrics.total_tasks) * 100)
    : 0;

  return (
    <Box>
      {/* Execution Status */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Execution Status</Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Chip
              icon={stateIcons[status.state]}
              label={status.state}
              color={stateColors[status.state]}
              size="small"
            />
            <IconButton size="small" onClick={loadStatus}>
              <RefreshIcon />
            </IconButton>
          </Box>
        </Box>

        {status.message && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {status.message}
          </Typography>
        )}

        {status.celery_task_id && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Task ID: {status.celery_task_id}
          </Typography>
        )}

        {/* Progress Bar */}
        {status.metrics && (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">Progress</Typography>
              <Typography variant="body2">{progressPercentage}%</Typography>
            </Box>
            <LinearProgress variant="determinate" value={progressPercentage} />
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">
                  Completed
                </Typography>
                <Typography variant="body2">{status.metrics.completed_tasks}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">
                  Failed
                </Typography>
                <Typography variant="body2" color="error">
                  {status.metrics.failed_tasks}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">
                  Total Tasks
                </Typography>
                <Typography variant="body2">{status.metrics.total_tasks}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">
                  Time (s)
                </Typography>
                <Typography variant="body2">{status.metrics.execution_time.toFixed(2)}</Typography>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Retry Information */}
        {status.retry_info && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              Retry Attempt #{status.retry_info.attempts}
            </Typography>
            <Typography variant="caption">{status.retry_info.reason}</Typography>
          </Alert>
        )}

        {/* Error Information */}
        {status.error && (
          <Accordion sx={{ mt: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography color="error">Error Details</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="error" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                {status.error}
              </Typography>
              {status.traceback && (
                <Typography variant="caption" sx={{ mt: 1, display: 'block', fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                  {status.traceback}
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>
        )}
      </Paper>

      {/* System Health */}
      {status.system_health && (
        <>
          {/* Circuit Breakers */}
          {Object.keys(status.system_health.circuit_breakers || {}).length > 0 && (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircuitIcon />
                Circuit Breakers
              </Typography>
              <Grid container spacing={2}>
                {Object.entries(status.system_health.circuit_breakers).map(([name, breaker]) => (
                  <Grid item xs={12} sm={6} key={name}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle2" gutterBottom>
                          {name.split('_').slice(0, -1).join(' ')}
                        </Typography>
                        <Chip
                          label={breaker.state.toUpperCase()}
                          color={circuitStateColors[breaker.state]}
                          size="small"
                          sx={{ mb: 1 }}
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Failures: {breaker.failure_count}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Successes: {breaker.success_count}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          )}

          {/* Agent Pools */}
          {Object.keys(status.system_health.agent_pools || {}).length > 0 && (
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <PoolIcon />
                Agent Pools
              </Typography>
              <Grid container spacing={2}>
                {Object.entries(status.system_health.agent_pools).map(([name, pool]) => (
                  <Grid item xs={12} sm={6} key={name}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle2" gutterBottom>
                          {name.split(':')[1] || name}
                        </Typography>
                        <Chip
                          icon={pool.healthy ? <SuccessIcon /> : <ErrorIcon />}
                          label={pool.healthy ? 'HEALTHY' : 'UNHEALTHY'}
                          color={pool.healthy ? 'success' : 'error'}
                          size="small"
                          sx={{ mb: 1 }}
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Pool Size: {pool.pool_size}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Active: {pool.active}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          )}
        </>
      )}
    </Box>
  );
}
